import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const timeToMinutes = (s) => {
    if (!s || s === '--:--') return -1;
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
};

const StateScreen = ({ msg, isError }) => (
    <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        direction: 'rtl',
    }}>
        <span style={{ fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8' }}>{msg}</span>
    </div>
);

const Schedule = ({
    moadData, processedCustomTimes, loading, error,
    title = 'מועד', showTitle = true, days = [],
    entryIcon = 'Sun', exitIcon = 'Star', dayLabels = [],
    drashotByDay = {},
    showLeyning, showHaftarah, leyningDisplayMode,
}) => {
    const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['Sun'];
    const ExitIcon  = WIDGET_ICON_MAP[exitIcon]  || WIDGET_ICON_MAP['Star'];

    if (loading) return <StateScreen msg="טוען זמני מועד..." />;
    if (error)   return <StateScreen msg={error} isError />;
    if (!moadData?.days?.length) return <StateScreen msg="אין נתוני מועד" />;

    const hasEntryTimes = moadData.days.some(d => d.entryTime !== null);
    const hasExitTimes  = moadData.days.some(d => d.exitTime  !== null);
    const scheduleGridCols = ['1fr', hasEntryTimes && '100px', hasExitTimes && '100px'].filter(Boolean).join(' ');

    // Group and sort custom times per day
    const timesByDay = {};
    (processedCustomTimes || []).forEach(ct => {
        const idx = ct.dayIndex ?? 0;
        if (!timesByDay[idx]) timesByDay[idx] = [];
        timesByDay[idx].push(ct);
    });
    Object.values(timesByDay).forEach(g =>
        g.sort((a, b) => (a._sortKey ?? timeToMinutes(a.time)) - (b._sortKey ?? timeToMinutes(b.time)))
    );

    return (
        <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            direction: 'rtl', boxSizing: 'border-box',
            backgroundColor: '#fdfbf7',
            overflow: 'hidden',
        }}>
            {/* Header */}
            {showTitle && (
                <div style={{
                    padding: '18px 24px',
                    borderBottom: '2px solid #1e3a5f',
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <span style={{
                        fontSize: '1.375rem', fontWeight: 700,
                        color: '#1e3a5f', letterSpacing: '0.02em',
                    }}>
                        {title}
                    </span>
                </div>
            )}

            {/* Column headers */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: scheduleGridCols,
                padding: '8px 24px',
                backgroundColor: '#f1ece4',
                borderBottom: '1px solid #e5ddd3',
                flexShrink: 0,
                gap: 8,
            }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em' }}>
                    יום
                </span>
                {hasEntryTimes && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <EntryIcon style={{ width: 12, height: 12, color: '#166534' }} />
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em' }}>
                            כניסה
                        </span>
                    </div>
                )}
                {hasExitTimes && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <ExitIcon style={{ width: 12, height: 12, color: '#1e3a5f' }} />
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em' }}>
                            יציאה
                        </span>
                    </div>
                )}
            </div>

            {/* Day rows */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {moadData.days.map((day, dayIdx) => {
                    const dayColor = days[dayIdx]?.color || '#166534';
                    const dayTimes = timesByDay[dayIdx] || [];
                    const isLast = dayIdx === moadData.days.length - 1;

                    return (
                        <div key={dayIdx} style={{
                            borderBottom: isLast ? 'none' : '1px solid #e5ddd3',
                        }}>
                            {/* Main row */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: scheduleGridCols,
                                padding: '14px 24px',
                                gap: 8,
                                alignItems: 'center',
                                borderRight: `3px solid ${dayColor}`,
                            }}>
                                <span style={{
                                    fontSize: '1.05rem', fontWeight: 700,
                                    color: '#1e3a5f',
                                }}>
                                    {day.label}
                                </span>
                                {hasEntryTimes && (day.entryTime !== null
                                    ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                        {dayLabels[dayIdx]?.entry && (
                                            <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                                {dayLabels[dayIdx].entry}
                                            </span>
                                        )}
                                        <span style={{ fontSize: '1.375rem', fontWeight: 700, color: '#166534', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{day.entryTime}</span>
                                      </div>
                                    : <span style={{ fontSize: '0.8rem', color: '#cbd5e1', textAlign: 'center' }}>—</span>
                                )}
                                {hasExitTimes && (day.exitTime !== null
                                    ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                        {dayLabels[dayIdx]?.exit && (
                                            <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                                {dayLabels[dayIdx].exit}
                                            </span>
                                        )}
                                        <span style={{ fontSize: '1.375rem', fontWeight: 700, color: '#1e3a5f', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{day.exitTime}</span>
                                      </div>
                                    : <span style={{ fontSize: '0.8rem', color: '#cbd5e1', textAlign: 'center' }}>—</span>
                                )}
                            </div>

                            {/* Leyning */}
                            {(showLeyning || showHaftarah) && day.leyning && (
                                <div style={{
                                    padding: '4px 24px 12px 24px',
                                    borderRight: `3px solid ${dayColor}`,
                                }}>
                                    <LeyningDisplay
                                        leyning={day.leyning}
                                        mode={leyningDisplayMode}
                                        showHaftarah={showHaftarah}
                                        showMaftir={leyningDisplayMode === 'full'}
                                        compact={true}
                                        style={{ padding: '8px 0' }}
                                    />
                                </div>
                            )}

                            {/* Custom times for this day */}
                            {dayTimes.length > 0 && (
                                <div style={{
                                    padding: '4px 24px 12px 24px',
                                    display: 'flex', flexWrap: 'wrap', gap: 6,
                                    borderRight: `3px solid ${dayColor}`,
                                }}>
                                    {dayTimes.map((ct, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '3px 10px',
                                            borderRadius: 99,
                                            backgroundColor: `${dayColor}12`,
                                            border: `1px solid ${dayColor}30`,
                                        }}>
                                            <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                                                {ct.label}
                                            </span>
                                            <span style={{
                                                fontSize: '0.875rem', fontWeight: 700,
                                                color: '#1e3a5f',
                                                fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                                            }}>
                                                {ct.time}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* דרשה */}
                            {drashotByDay[dayIdx] && (
                                <div style={{
                                    padding: '6px 24px 12px 24px',
                                    borderRight: `3px solid ${dayColor}`,
                                }}>
                                    <div style={{
                                        fontSize: '0.9rem', fontWeight: 600,
                                        color: '#1e3a5f', fontStyle: 'italic',
                                        whiteSpace: 'pre-wrap', lineHeight: 1.4,
                                    }}>
                                        {drashotByDay[dayIdx]}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Schedule;
